'use client';

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import {
  clearDeferredPrompt,
  getDeferredPrompt,
  getInstallSnapshot,
  subscribeInstallStore,
} from './installPromptStore';

export type InstallStatus =
  | 'unknown' // pre-mount / detection not yet run — render nothing
  | 'installed' // already running as an installed app
  | 'android' // Android/desktop Chromium with a captured beforeinstallprompt
  | 'ios' // iOS Safari — manual "Add to Home Screen" only (no install API)
  | 'unsupported'; // this browser can't install to the home screen

// True when the page is running as an installed (standalone) app.
function detectStandalone(): boolean {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true || nav.standalone === true // iOS Safari's non-standard flag
  );
}

// True only for real iOS Safari, where "Add to Home Screen" exists but there is
// no programmatic install API. Excludes iOS Chrome/Firefox/Edge/Opera (which
// can't A2HS) and common in-app webviews (where the Share sheet lacks it).
function detectIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  const isIos =
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ reports a desktop Mac UA but is touch-capable.
    (/macintosh/i.test(ua) && window.navigator.maxTouchPoints > 1);
  if (!isIos) return false;
  const nonSafariShell = /crios|fxios|edgios|opios|mercury/i.test(ua);
  const inAppWebview = /fbav|fban|instagram|line|micromessenger|gsa|twitter/i.test(ua);
  return /safari/i.test(ua) && !nonSafariShell && !inAppWebview;
}

/**
 * useInstallPrompt — resolves how (and whether) this browser can install the app
 * to the home screen, and exposes the Android install trigger.
 *
 * Reactivity: `useSyncExternalStore` tracks the module-scope capture bridge, so
 * a `beforeinstallprompt` that arrives after mount upgrades the status live.
 * Browser detection (iOS/standalone) runs once, post-mount, behind a gate so the
 * static-export prerender never sees `navigator` and there's no hydration flash
 * — the status is `'unknown'` (render nothing) until the client effect runs.
 */
export function useInstallPrompt(): { status: InstallStatus; promptInstall: () => Promise<void> } {
  const snapshot = useSyncExternalStore(subscribeInstallStore, getInstallSnapshot, () => 'none');
  const [client, setClient] = useState<{ standalone: boolean; iosSafari: boolean } | null>(null);

  useEffect(() => {
    setClient({ standalone: detectStandalone(), iosSafari: detectIosSafari() });
  }, []);

  let status: InstallStatus = 'unknown';
  if (client) {
    if (snapshot === 'installed' || client.standalone) status = 'installed';
    else if (snapshot === 'android') status = 'android';
    else if (client.iosSafari) status = 'ios';
    else status = 'unsupported';
  }

  const promptInstall = useCallback(async () => {
    const evt = getDeferredPrompt();
    if (!evt) return;
    try {
      await evt.prompt();
      const { outcome } = await evt.userChoice;
      // `userChoice` resolves at the user's DECISION — for 'accepted' this is
      // BEFORE the OS finishes installing and fires `appinstalled`. Clearing the
      // event here would briefly drop status to 'unsupported' (event gone,
      // not-yet-installed) and flash a contradictory "can't install" message.
      // So on accept we do nothing: the `appinstalled` listener is the single
      // place that clears the event and flips status to 'installed'. Only a
      // decline needs an explicit clear (the event is spent; re-arming waits for
      // a fresh `beforeinstallprompt`).
      if (outcome !== 'accepted') clearDeferredPrompt();
    } catch {
      // prompt() threw (already consumed) or userChoice rejected — drop the
      // stale, unusable event.
      clearDeferredPrompt();
    }
  }, []);

  return { status, promptInstall };
}
